import React, { useState, useEffect } from 'react';
import { Car } from 'lucide-react';
import apiService from '../apiService';

const TYPE_LABELS = { mbiyo: 'Mbiyo', confort: 'Confort', premium: 'Premium', covoiturage: 'Covoiturage', moto: 'Moto' };
const typeLabel = (t) => TYPE_LABELS[t] || t || '—';
// Mono-type : « Mbiyo ». Multi-types : « Mbiyo + Confort → Confort » une fois acceptée ;
// « Mbiyo + Confort · 6 000–7 800 CDF » tant que la course cherche (prix selon le chauffeur).
function renderRideType(ride) {
  const types = Array.isArray(ride.ride_types) && ride.ride_types.length > 1 ? ride.ride_types : null;
  if (!types) return <span>{typeLabel(ride.ride_type)}</span>;
  const vals = types.map(t => ride.type_prices?.[t]?.estimated_price).filter(v => v != null);
  const range = vals.length ? `${Math.min(...vals).toLocaleString('fr-FR')}–${Math.max(...vals).toLocaleString('fr-FR')} CDF` : null;
  const accepted = !!ride.driver_id;
  return (
    <span title="Course multi-types : le client acceptait plusieurs types">
      <span style={{ fontWeight: 600 }}>{types.map(typeLabel).join(' + ')}</span>
      {accepted ? <span> → <strong>{typeLabel(ride.ride_type)}</strong></span> : (range ? <span style={{ color: '#9AA0A8' }}> · {range}</span> : null)}
    </span>
  );
}

export default function TaxiRidesPage() {
  const [taxiRides, setTaxiRides] = useState([]);
  const [taxiStats, setTaxiStats] = useState(null);
  const [taxiLoading, setTaxiLoading] = useState(false);
  const [taxiFilter, setTaxiFilter] = useState('');
  const [taxiPage, setTaxiPage] = useState(1);
  const [taxiTotal, setTaxiTotal] = useState(0);

  useEffect(() => { loadTaxiStats(); loadTaxiRides(); }, []);
  useEffect(() => { loadTaxiRides(); }, [taxiFilter, taxiPage]);

  const loadTaxiStats = async () => {
    try {
      const res = await apiService.get('/taxi/admin/stats', { userType: 'admin' });
      if (res.success) setTaxiStats(res.stats);
    } catch (err) { console.error('Erreur stats taxi:', err); }
  };

  const loadTaxiRides = async () => {
    setTaxiLoading(true);
    try {
      const params = new URLSearchParams({ page: taxiPage, limit: 20 });
      if (taxiFilter) params.append('status', taxiFilter);
      const res = await apiService.get(`/taxi/admin/rides?${params}`, { userType: 'admin' });
      if (res.success) { setTaxiRides(res.rides || []); setTaxiTotal(res.total || 0); }
    } catch (err) { console.error('Erreur rides taxi:', err); }
    setTaxiLoading(false);
  };

  const statusColors = {
    searching: '#FFB900', accepted: '#FF7800', driver_arriving: '#FF7800',
    in_progress: '#10B981', completed: '#059669', cancelled_client: '#DC2626',
    cancelled_driver: '#dc2626', no_driver_found: '#5C636E'
  };
  const statusLabels = {
    searching: 'En recherche', accepted: 'Acceptée', driver_arriving: 'Chauffeur en route',
    in_progress: 'En cours', completed: 'Terminée', cancelled_client: 'Annulée (client)',
    cancelled_driver: 'Annulée (chauffeur)', no_driver_found: 'Pas de chauffeur'
  };

  return (
    <div className="dashboard-content">
      <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Car size={28} /> Gestion des Courses Taxi
      </h1>

      {taxiStats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: "Aujourd'hui", value: taxiStats.total_today, color: '#1C1C1C' },
            { label: 'En cours', value: taxiStats.active_now, color: '#FFB900' },
            { label: 'Terminées (mois)', value: taxiStats.completed_month, color: '#10B981' },
            { label: 'Taux annulation', value: `${taxiStats.cancellation_rate}%`, color: '#DC2626' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background: 'white', borderRadius: '12px', padding: '1.2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: '0.85rem', color: '#5C636E', marginBottom: '4px' }}>{label}</div>
              <div style={{ fontSize: '1.8rem', fontWeight: '700', color }}>{value}</div>
            </div>
          ))}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: '0.85rem', color: '#5C636E', marginBottom: '4px' }}>Revenu (mois)</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#C9350F' }}>{(taxiStats.revenue_month || 0).toLocaleString('fr-FR')} CDF</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button onClick={() => { setTaxiFilter(''); setTaxiPage(1); }}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', background: !taxiFilter ? '#F54119' : '#F4F5F7', color: !taxiFilter ? 'white' : '#5C636E' }}>
          Toutes
        </button>
        {Object.entries(statusLabels).map(([key, label]) => (
          <button key={key} onClick={() => { setTaxiFilter(key); setTaxiPage(1); }}
            style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', background: taxiFilter === key ? statusColors[key] : '#F4F5F7', color: taxiFilter === key ? 'white' : '#5C636E', fontSize: '0.8rem' }}>
            {label}
          </button>
        ))}
      </div>

      {taxiLoading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement...</p></div>
      ) : (
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#FFFFFF' }}>
                  {['ID', 'Client', 'Chauffeur', 'Type', 'Trajet', 'Prix', 'Statut', 'Date'].map(h => (
                    <th key={h} style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#5C636E' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {taxiRides.length === 0 && (
                  <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#9AA0A8' }}>Aucune course trouvée</td></tr>
                )}
                {taxiRides.map(ride => (
                  <tr key={ride.ride_id} style={{ borderTop: '1px solid #F4F5F7' }}>
                    <td style={{ padding: '12px' }}><span style={{ fontSize: '0.75rem', color: '#5C636E', fontFamily: 'monospace' }}>{ride.ride_id?.slice(-8)}</span></td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{ride.client_name || '—'}</td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{ride.driver_name || '—'}</td>
                    <td style={{ padding: '12px', fontSize: '0.8rem' }}>{renderRideType(ride)}</td>
                    <td style={{ padding: '12px', maxWidth: '200px' }}>
                      <div style={{ fontSize: '0.8rem', color: '#414852', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ride.pickup?.address}</div>
                      <div style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>→ {ride.dropoff?.address}</div>
                    </td>
                    <td style={{ padding: '12px', fontWeight: '600', color: '#C9350F' }}>{(ride.final_price || ride.estimated_price || 0).toLocaleString('fr-FR')} CDF</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', background: `${statusColors[ride.status] || '#5C636E'}20`, color: statusColors[ride.status] || '#5C636E' }}>
                        {statusLabels[ride.status] || ride.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.8rem', color: '#5C636E' }}>{new Date(ride.created_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {taxiTotal > 20 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '1rem' }}>
              <button onClick={() => setTaxiPage(p => Math.max(1, p - 1))} disabled={taxiPage <= 1}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #E9EBEF', background: 'white', cursor: taxiPage <= 1 ? 'not-allowed' : 'pointer', color: '#414852' }}>
                ← Précédent
              </button>
              <span style={{ display: 'flex', alignItems: 'center', color: '#5C636E', fontSize: '0.9rem' }}>Page {taxiPage} / {Math.ceil(taxiTotal / 20)}</span>
              <button onClick={() => setTaxiPage(p => p + 1)} disabled={taxiPage >= Math.ceil(taxiTotal / 20)}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #E9EBEF', background: 'white', cursor: taxiPage >= Math.ceil(taxiTotal / 20) ? 'not-allowed' : 'pointer', color: '#414852' }}>
                Suivant →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
